import React, { useState } from 'react';
import { IFilterMultiChooseProps } from '../types';

/**
 * Хук контролирует работу фильтра MultiChoose
 * Хранит состояние фильтра, отдает коллбэк для его обновления
 * Вызывает внешний коллбэк onSelectItem при обновлении состояния
 * */
export const useMultiChooseController = <T extends string | number>(props: IFilterMultiChooseProps<T>) => {
  const { onSelectItem, items, label } = props;
  const [selectedItems, setSelectedItems] = useState<Set<T>>(new Set());

  const handleSelectItem = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    event => {
      const value = (event.target.value as unknown) as T;
      const newSelectedItems = new Set(selectedItems);

      if (selectedItems.has(value)) {
        newSelectedItems.delete(value);
      } else {
        newSelectedItems.add(value);
      }

      setSelectedItems(newSelectedItems);
    },
    [selectedItems],
  );

  const selectedItemsUiLabel = React.useMemo(() => {
    if (!selectedItems.size) {
      return label;
    }

    return Array.from(selectedItems)
      .map(selectedItemKey => items.get(selectedItemKey))
      .join(', ');
  }, [items, label, selectedItems]);

  const itemsArray = React.useMemo(() => Array.from(items), [items]);

  React.useEffect(() => {
    onSelectItem(Array.from(selectedItems));
  }, [onSelectItem, selectedItems]);

  return { selectedItems, handleSelectItem, selectedItemsUiLabel, itemsArray };
};
